import type { FC, ReactNode } from 'react';

/**
 * One visual vocabulary for every Account section (Leo, 2026-09-19: the dialog
 * mixed three heading sizes and three button styles). Sections render through
 * AccountSection; buttons and fields import the classes below.
 */

export const primaryBtn =
  'inline-flex items-center justify-center rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-black transition hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-50';
/**
 * Copper gradient fill for the dashboard's call-to-action buttons. Dark text,
 * never white (DASH-020): #181818 measures 6.9:1 on primary-500 and 5.2:1 on
 * primary-600; white was 2.6-3.4:1. Hover lightens (primary-400 to -500, 8.8 and
 * 6.9:1), because darkening to primary-700 would drop dark text to 3.6:1.
 */
export const copperFill =
  'bg-gradient-to-r from-primary-500 to-primary-600 text-[#181818] hover:from-primary-400 hover:to-primary-500';
/** Small ghost button that sits inside a value box (API key row), beside the value. */
export const inlineBtn =
  'rounded-md border border-white/10 px-3 py-1.5 text-sm font-medium text-gray-200 transition hover:border-[#e6a98c]/50 hover:text-white disabled:opacity-50';

/** Filled copper variant of inlineBtn: the irreversible action on a key row
 *  (Revoke) reads as the one that cannot be rolled back (Leo, 2026-09-20). */
export const inlineDangerBtn =
  'rounded-md bg-primary-500 px-3 py-1.5 text-sm font-semibold text-black transition hover:bg-primary-400 disabled:opacity-50';

export const ghostBtn =
  'inline-flex items-center justify-center rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-gray-200 transition hover:border-white/20 hover:bg-white/10 disabled:opacity-50';
/** Every text input answers hover and focus in copper (Leo, 2026-09-20). */
export const fieldInteractionCls =
  'transition hover:border-[#e6a98c]/50 focus:border-[#e6a98c] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#e6a98c]/60';
export const fieldCls = `w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-gray-500 ${fieldInteractionCls}`;
export const fieldLabelCls = 'block text-sm text-gray-300';
export const fieldLabelTextCls = 'mb-1.5 block';
export const checkboxCls = 'h-4 w-4 rounded border-white/20 bg-black/30 accent-[#e6a98c]';

interface AccountSectionProps {
  testId: string;
  /** Omit when the page title above already names the section. */
  eyebrow?: string;
  heading: string;
  subhead?: ReactNode;
  /** Right-aligned control in the header row (e.g. "Create new key"). */
  action?: ReactNode;
  sectionRef?: React.Ref<HTMLElement>;
  children: ReactNode;
}

export const AccountSection: FC<AccountSectionProps> = ({
  testId,
  eyebrow,
  heading,
  subhead,
  action,
  sectionRef,
  children,
}) => (
  <section
    ref={sectionRef}
    data-testid={testId}
    className="ce-card rounded-xl border border-white/10 bg-white/5 p-5 sm:p-6"
  >
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        {eyebrow && (
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-gray-500">
            {eyebrow}
          </p>
        )}
        <h2 className="text-lg font-semibold text-white">{heading}</h2>
        {subhead && <p className="mt-1 text-sm leading-relaxed text-gray-400">{subhead}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);
